(() => {
  const root = document.querySelector('[data-h17-cookie-consent]');
  if (!root || root.dataset.initialized === 'true') return;

  root.dataset.initialized = 'true';

  const banner = root.querySelector('[data-h17-cookie-banner]');
  const dialog = root.querySelector('[data-h17-cookie-dialog]');
  const status = root.querySelector('[data-h17-cookie-status]');
  const savedMessage = root.querySelector('[data-h17-cookie-saved-message]')?.textContent?.trim() || '';
  const errorMessage = root.querySelector('[data-h17-cookie-error-message]')?.textContent?.trim() || '';
  const choiceInputs = Array.from(root.querySelectorAll('[data-h17-cookie-choice]'));
  const actionButtons = Array.from(root.querySelectorAll('[data-h17-cookie-action]'));

  let customerPrivacy = null;
  let loadingPrivacyApi = false;
  let apiCallbacks = [];
  let previouslyFocused = null;
  let consentRequestId = 0;
  let apiLoadId = 0;

  // This only remembers dismissal for this tab's visit. It never grants tracking
  // consent, reads Shopify cookies, or replays a choice without a visitor action.
  const dismissalKey = 'h17-cookie-banner-decision';
  let decisionState = '';
  try {
    const savedState = window.sessionStorage.getItem(dismissalKey);
    if (savedState === 'pending' || savedState === 'saved') decisionState = savedState;
  } catch (error) {
    // Storage can be blocked; dismissal must still work on the current page.
  }

  const rememberDecision = (state) => {
    decisionState = state;
    try {
      window.sessionStorage.setItem(dismissalKey, state);
    } catch (error) {
      // Do not turn a storage restriction into an undismissable banner.
    }
  };

  const setBannerVisible = (visible) => {
    if (!banner) return;
    banner.hidden = !visible;
    banner.setAttribute('aria-hidden', String(!visible));

    if (visible) {
      banner.style.removeProperty('display');
    } else {
      banner.style.setProperty('display', 'none', 'important');
    }
  };

  const setBusy = (busy) => {
    actionButtons.forEach((button) => {
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
    });
  };

  const setStatus = (message, isError = false) => {
    if (!status) return;
    status.textContent = message;
    status.dataset.error = String(isError);
  };

  const flushApiCallbacks = (api) => {
    const callbacks = apiCallbacks;
    apiCallbacks = [];
    callbacks.forEach((callback) => callback(api));
  };

  const loadPrivacyApi = (callback) => {
    if (customerPrivacy) {
      callback(customerPrivacy);
      return;
    }

    apiCallbacks.push(callback);
    if (loadingPrivacyApi) return;
    loadingPrivacyApi = true;

    if (window.Shopify?.customerPrivacy) {
      customerPrivacy = window.Shopify.customerPrivacy;
      loadingPrivacyApi = false;
      flushApiCallbacks(customerPrivacy);
      return;
    }

    if (typeof window.Shopify?.loadFeatures !== 'function') {
      loadingPrivacyApi = false;
      flushApiCallbacks(null);
      return;
    }

    const loadId = ++apiLoadId;
    let completed = false;
    const finishLoading = (error) => {
      if (completed || loadId !== apiLoadId) return;
      completed = true;
      window.clearTimeout(timeoutId);
      loadingPrivacyApi = false;
      customerPrivacy = error ? null : window.Shopify?.customerPrivacy || null;
      flushApiCallbacks(customerPrivacy);
    };
    const timeoutId = window.setTimeout(() => finishLoading(new Error('Privacy API load timed out.')), 5000);

    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], finishLoading);
    } catch (error) {
      finishLoading(error);
    }
  };

  const currentConsent = () => {
    if (!customerPrivacy || typeof customerPrivacy.currentVisitorConsent !== 'function') return {};

    try {
      return customerPrivacy.currentVisitorConsent() || {};
    } catch (error) {
      console.warn('Hangar 17 cookie preferences could not read the current consent state.', error);
      return {};
    }
  };

  const seedChoices = () => {
    const consent = currentConsent();
    choiceInputs.forEach((input) => {
      input.checked = consent[input.dataset.h17CookieChoice] === 'yes';
    });
  };

  const showPreferencesDialog = () => {
    if (typeof dialog?.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog?.setAttribute('open', '');
    }
  };

  const openPreferences = (trigger) => {
    previouslyFocused = trigger instanceof HTMLElement ? trigger : document.activeElement;
    setStatus(decisionState === 'pending' ? errorMessage : '', decisionState === 'pending');

    loadPrivacyApi((api) => {
      if (!api) {
        setStatus(errorMessage, true);
      }

      seedChoices();
      showPreferencesDialog();
    });
  };

  const closePreferences = () => {
    if (typeof dialog?.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog?.removeAttribute('open');
    }
  };

  const saveConsent = (consent) => {
    const requestId = ++consentRequestId;
    let completed = false;

    rememberDecision('pending');
    setStatus('');
    setBusy(true);
    setBannerVisible(false);
    closePreferences();

    const timeoutId = window.setTimeout(() => {
      if (completed || requestId !== consentRequestId) return;
      setBusy(false);
      setStatus(errorMessage, true);
      console.warn('Hangar 17 cookie preferences were submitted, but Shopify did not confirm the update in time.');
    }, 5000);

    const finish = (error = null) => {
      if (completed || requestId !== consentRequestId) return;
      completed = true;
      window.clearTimeout(timeoutId);
      setBusy(false);

      if (error) {
        setStatus(errorMessage, true);
        return;
      }

      rememberDecision('saved');
      setStatus(savedMessage);
    };

    loadPrivacyApi((api) => {
      if (!api || typeof api.setTrackingConsent !== 'function') {
        finish(new Error('Shopify Customer Privacy API is unavailable.'));
        return;
      }

      try {
        api.setTrackingConsent(consent, (result) => {
          finish(result?.error || null);
        });
      } catch (error) {
        finish(error);
      }
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element
      ? event.target.closest('[data-h17-cookie-preferences-trigger]')
      : null;

    if (!trigger) return;
    event.preventDefault();
    openPreferences(trigger);
  });

  root.querySelectorAll('[data-h17-cookie-close]').forEach((button) => {
    button.addEventListener('click', closePreferences);
  });

  root.querySelector('[data-h17-cookie-action="accept"]')?.addEventListener('click', () => {
    saveConsent({ analytics: true, marketing: true, preferences: true });
  });

  root.querySelector('[data-h17-cookie-action="reject"]')?.addEventListener('click', () => {
    saveConsent({ analytics: false, marketing: false, preferences: false });
  });

  root.querySelector('[data-h17-cookie-action="save"]')?.addEventListener('click', () => {
    const consent = {};
    choiceInputs.forEach((input) => {
      consent[input.dataset.h17CookieChoice] = input.checked;
    });
    saveConsent(consent);
  });

  dialog?.addEventListener('close', () => {
    if (previouslyFocused instanceof HTMLElement && !previouslyFocused.closest('[hidden]')) {
      previouslyFocused.focus();
    }
  });

  loadPrivacyApi((api) => {
    if (!api || window.Shopify?.designMode) return;

    const consent = currentConsent();
    const choices = ['analytics', 'marketing', 'preferences'];
    const hasRecordedChoice = choices.some((choice) => consent[choice] === 'yes' || consent[choice] === 'no');
    let shouldShow = false;

    try {
      shouldShow = typeof api.shouldShowBanner === 'function' && api.shouldShowBanner();
    } catch (error) {
      console.warn('Hangar 17 cookie preferences could not determine banner visibility.', error);
    }

    // An error or late API response must not reopen a banner the visitor dismissed.
    setBannerVisible(!decisionState && (shouldShow || !hasRecordedChoice));
  });
})();
